import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useStrategyMapService, StrategyMapListItem } from '../../services/strategyMap';
import EntityItem from './EntityItem';
import EntityItemSkeleton from './EntityItemSkeleton';
import StyledSelect from '../../shared/atoms/StyledSelect';
import { FaSearch } from 'react-icons/fa';
import './SelectEntities.css';
import SortByDropdown from '../../components/common/SortByDropdown';

interface SelectEntitiesProps {
  defaultSelectedItems: StrategyMapListItem[];
  onChange: (selectedEntities: StrategyMapListItem[]) => void;
  entityTypeLabel: string;
  searchFilterConfig?: {visibility?: boolean, value?: string};
  pinnedItems?: StrategyMapListItem[];
  onBeforeToggle?: (item: StrategyMapListItem, isSelected: boolean) => boolean;
  selectType?: 'single' | 'multiple';
  defaultSelectedIds?: { [key: string]: string[] };
  enableNoRelationSelection?: boolean;
}

const searchLabels = {
  epic: "Epics",
  container_epic: "Advanced Epics",
  executing_epic: "Normal Epics",
  all: "Epics and Features",
  feature: "Features"
}

const SelectEntities: React.FC<SelectEntitiesProps> = ({
  defaultSelectedItems,
  onChange,
  searchFilterConfig,
  pinnedItems = [],
  onBeforeToggle,
  selectType = 'multiple',
  defaultSelectedIds,
  enableNoRelationSelection = true
}) => {
  const { fetchEntities, fetchEntitiesByIds } = useStrategyMapService();
  const [items, setItems] = useState<StrategyMapListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchType, setSearchType] = useState(searchFilterConfig?.value ?? "all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<StrategyMapListItem[]>(defaultSelectedItems ?? []);
  const [internalPinnedItems, setInternalPinnedItems] = useState<StrategyMapListItem[]>(pinnedItems);
  const [sortBy, setSortBy] = useState("modified_at_desc");
  
  const searchDebounceRef = React.useRef<NodeJS.Timeout | null>(null);

  // Sync prop pinnedItems to state
  useEffect(() => {
    setInternalPinnedItems(prev => {
       const existingIds = new Set(prev.map(i => i.id));
       const newItems = pinnedItems.filter(i => !existingIds.has(i.id));
       // Also handle removals? If prop changes completely? Assuming append/update behavior mostly.
       // Let's just merge prop into state for now to be safe, or direct sync?
       // If prop is source of truth + fetched additions...
       // Let's assume prop is initial or primary source.
       return [...pinnedItems, ...prev.filter(p => !pinnedItems.some(pi => pi.id === p.id))]; 
    });
  }, [JSON.stringify(pinnedItems)]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(true);


  const loadEntities = useCallback(async (search: string, type: string, pageNum: number, append: boolean = false, sort: string) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const perPage = 20;
      const response = await fetchEntities(pageNum, perPage, search, type, sort);
      
      if (append) {
        setItems(prev => [...prev, ...response.items]);
      } else {
        setItems(response.items);
      }

      setTotalItems(response.total ?? 0);
      setHasMore(response.items.length >= perPage);
    } catch (error) {
      console.error('Error fetching entities:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  const reloadData = useCallback(() => {
    setItems([]);
    setTotalItems(0);
    setHasMore(true);
    setPage(1);
    loadEntities(debouncedSearchTerm, searchType, 1, false, sortBy);
  }, [debouncedSearchTerm, searchType, sortBy]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  useEffect(()=>{
    if(searchFilterConfig?.value) setSearchType(searchFilterConfig.value);
    
  }, [searchFilterConfig?.value])
  useEffect(() => {
    
    if(debouncedSearchTerm !== "" || searchType !== "" || sortBy !== ""){
      reloadData();
    }
  }, [debouncedSearchTerm, searchType, sortBy]);


  useEffect(()=>{
    if(defaultSelectedItems && defaultSelectedItems.length > 0) {
      setSelectedItems(defaultSelectedItems);
      setSelectedIds(defaultSelectedItems.map(item => item.id));
    }
  }, [defaultSelectedItems])

  useEffect(() => {
    const fetchDefaultIds = async () => {
        if (defaultSelectedIds && Object.keys(defaultSelectedIds).length > 0) {
            const roadmapIds = defaultSelectedIds['roadmap_ids'] || [];
            const projectIds = defaultSelectedIds['project_ids'] || [];

            if (roadmapIds.length === 0 && projectIds.length === 0) return;

             try {
                const response = await fetchEntitiesByIds(roadmapIds, projectIds);
                if (response && Array.isArray(response)) {
                    setSelectedItems(prev => {
                         // Merge with existing selected items, avoiding duplicates
                         const existingIds = new Set(prev.map(i => i.id));
                         const newItems = response.filter((item: StrategyMapListItem) => !existingIds.has(item.id));
                         return [...prev, ...newItems];
                    });
                     // Set pinned items from default selected
                    setInternalPinnedItems(prev => {
                        const existingIds = new Set(prev.map(i => i.id));
                        const newItems = response.filter((item: StrategyMapListItem) => !existingIds.has(item.id));
                        return [...prev, ...newItems];
                    });
                    setSelectedIds(prev => {
                        const newIds = response.map((item: StrategyMapListItem) => item.id).filter((id: string) => !prev.includes(id));
                        return [...prev, ...newIds];
                    });
                }
             } catch (error) {
                 console.error("Failed to fetch default selected entities", error);
             }
             reloadData();
        }
    }
    fetchDefaultIds();
  }, [JSON.stringify(defaultSelectedIds??[])]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && !loading && !loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadEntities(debouncedSearchTerm, searchType, nextPage, true, sortBy);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchTerm(val);

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(val);
    }, 400);
  };

  const handleTypeChange = (e: { target: { value: string } }) => {
    setSearchType(e.target.value);
  };
    const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSortBy(e.target.value);
    };

  const handleToggle = (item: StrategyMapListItem) => {
    const isSelected = selectedIds.includes(item.id);
    
    if (onBeforeToggle) {
      const allowed = onBeforeToggle(item, isSelected);
      if (!allowed) return;
    }

    let newSelected: StrategyMapListItem[];

    if (selectType === 'single') {
        // Single selection: clicking always selects the item (replacing current selection)
        // Unless it's already selected and we want to allow deselect? Usually radio buttons don't deselect.
        // Let's assume standard behavior: clicking selects it. If already selected, it stays selected.
        // Wait, if users want to unselect, they might need to clear selection. But radio buttons are usually mandatory once selected.
        // Given "Select Parent Epic" could be optional, maybe allowing toggle off is good? 
        // But "radio button" implies 1 choice.
        // I'll make it select. If they want to clear, they might need a clear button outside?
        // Or if they click again it deselects? A radio button that deselects itself is a checkbox behavior visually rendered as radio? No.
        // I will stick to "Replace selection".
        if (isSelected) {
            // Already selected, do nothing or allow deselect?
            // If I allow deselect: newSelected = []
            // If I don't: newSelected = [item]
            // Let's allow deselect for flexibility unless it feels wrong.
            // Actually, for "Parent Epic", it might be optional. So deselect is good.
            // But with radio button UI, users expect it to stay selected.
            // I'll make it toggle for now, as it's safer for "optional" fields without a clear button.
             newSelected = isSelected ? [] : [item];
        } else {
             newSelected = [item];
        }
    } else {
        // Multiple selection logic (existing)
        if (isSelected) {
          newSelected = selectedItems.filter(i => i.id !== item.id);
        } else {
          newSelected = [...selectedItems, item];
        }
    }
    
    setSelectedItems(newSelected);
    setSelectedIds(newSelected.map(i => i.id));
    onChange(newSelected);
  };

  const pinnedIds = internalPinnedItems.map(p => p.id);
  const filteredItems = items.filter(item => !pinnedIds.includes(item.id));

  // Compute disabled states for Epic↔Feature conflict prevention
  const { disabledIds, disabledReasons } = useMemo(() => {
    if (!enableNoRelationSelection) {
      return { disabledIds: new Set<string>(), disabledReasons: new Map<string, string>() };
    }

    const disabled = new Set<string>();
    const reasons = new Map<string, string>();

    // Combine all known items for parent-child lookups.
    // IMPORTANT: API items (items) must come LAST so their child_entities data
    // takes priority over selectedItems/pinnedItems (which come from strategy map
    // fetch and often lack child_entities).
    const uniqueItems = new Map<string, StrategyMapListItem>();
    [...selectedItems, ...internalPinnedItems, ...items].forEach(item => uniqueItems.set(item.id, item));

    // Build a map of roadmap (Epic) ID → its child project (Feature) IDs
    // using child_entities from the API response
    const epicChildMap = new Map<string, string[]>();
    // Build a reverse map: project (Feature) ID → parent roadmap (Epic) info
    const featureParentMap = new Map<string, { parentId: string; parentName: string }[]>();

    uniqueItems.forEach(item => {
      if (item.type === 'roadmap') {
        // Use child_entities (from the API) to get the children
        const children: any[] = (item as any).child_entities || [];
        const childIds = children.map((c: any) => c.id);
        if (childIds.length > 0) {
          epicChildMap.set(item.id, childIds);
          childIds.forEach((childId: string) => {
            const parents = featureParentMap.get(childId) || [];
            parents.push({ parentId: item.id, parentName: item.name });
            featureParentMap.set(childId, parents);
          });
        }
      }
    });

    selectedItems.forEach(selected => {
      if (selected.type === 'roadmap') {
        // Epic is selected → disable all its child Features
        const childIds = epicChildMap.get(selected.id) || [];
        childIds.forEach(childId => {
          disabled.add(childId);
          reasons.set(
            childId,
            `Already included — this Feature is part of the selected Epic "${selected.name}".`
          );
        });
      } else if (selected.type === 'project') {
        // Feature is selected → disable the parent Epic that contains it
        const parents = featureParentMap.get(selected.id) || [];
        parents.forEach(({ parentId, parentName }) => {
          disabled.add(parentId);
          const existing = reasons.get(parentId);
          if (!existing) {
            reasons.set(
              parentId,
              `Cannot select this Epic — its Feature "${selected.name}" is already selected individually.`
            );
          }
        });
      }
    });

    return { disabledIds: disabled, disabledReasons: reasons };
  }, [selectedItems, items, internalPinnedItems, enableNoRelationSelection]);

  return (
    <div className="select-entities space-y-6 -mt-2 h-[60vh] w-full">
      {/* Search Bar & Filters */}
      <div className="flex gap-4">

        {
          (searchFilterConfig?.visibility ?? true) && (
            <div className="w-48">
              <StyledSelect 
                value={searchType}
                onChange={handleTypeChange}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'epic', label: 'Epics' },
                  { value: 'feature', label: 'Features' }
                ]}
              />
            </div>
          )
        }
        
        <div className="select-entities__search-wrapper flex-1">
          <div className="select-entities__search-input-container">
            <input
              type="text"
              className="select-entities__search-input"
              placeholder={`Search ${searchLabels?.[searchType as keyof typeof searchLabels] ?? searchType}`}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <div className="select-entities__search-icon">
             <FaSearch color='grey'/>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-light-text-tertiary dark:text-gray-400 px-1 -mb-4">
        <div className="flex gap-2 items-center">
            <span>Showing {filteredItems.length} of {totalItems} items</span>
            {/* {totalItems > 0 && (
            <span>(Page {page})</span>
            )} */}
        </div>
        <div className=" scale-90 origin-right z-20">
            <SortByDropdown
                value={sortBy}
                onChange={handleSortChange}
            />
        </div>
      </div>

      {/* Entity List */}
      <div 
        className="select-entities__project-list max-h-[400px] overflow-y-auto px-1"
        onScroll={handleScroll}
      >
        {loading && filteredItems.length === 0 ? (
          Array(5).fill(0).map((_, i) => <EntityItemSkeleton key={i} />)
        ) : filteredItems.length > 0 || internalPinnedItems.length > 0 ? (
          <>
            {internalPinnedItems.map((item) => (
              <EntityItem 
                key={item.id}
                item={item}
                isSelected={selectedIds.includes(item.id)}
                isDisabled={disabledIds.has(item.id)}
                tooltipMessage={disabledReasons.get(item.id)}
                onToggle={() => handleToggle(item)}
                selectType={selectType}
              />
            ))}
            {filteredItems.map((item) => (
              <EntityItem 
                key={item.id}
                item={item}
                isSelected={selectedIds.includes(item.id)}
                isDisabled={disabledIds.has(item.id)}
                tooltipMessage={disabledReasons.get(item.id)}
                onToggle={() => handleToggle(item)}
                selectType={selectType}
              />
            ))}
            {loadingMore && (
              <div className=" space-y-4">
                <EntityItemSkeleton />
                <EntityItemSkeleton />
                <EntityItemSkeleton />
              </div>
            )}
          </>
        ) : !loading && (
          <div className="text-center py-10 text-light-text-secondary dark:text-slate-400">
            No entities found.
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectEntities;
